import React, { useState, useEffect } from 'react';
import { highlightText } from './highlight';
import { pseudoLocalize } from './pseudo';

// Best-effort author label from a comment (populated object or raw id).
function authorName(author) {
    if (author && typeof author === 'object') {
        const name = [author.firstName, author.lastName].filter(Boolean).join(' ');
        return name || author.email || 'User';
    }
    return 'User';
}

function CommentThread({ getThread, sectionIndex }) {
    const [items, setItems] = useState(null); // null = loading

    useEffect(() => {
        let alive = true;
        getThread(sectionIndex).then((c) => { if (alive) setItems(c); });
        return () => { alive = false; };
    }, [getThread, sectionIndex]);

    if (items === null) {
        return <div style={{ fontSize: 12, color: 'var(--ink-4)', padding: '6px 0' }}>Loading comments…</div>;
    }
    if (!items.length) {
        return <div style={{ fontSize: 12, color: 'var(--ink-4)', padding: '6px 0' }}>No comments.</div>;
    }
    return (
        <div style={{
            marginTop: 6, paddingLeft: 10, borderLeft: '2px solid var(--line)',
            display: 'flex', flexDirection: 'column', gap: 8,
        }}>
            {items.map((c) => (
                <div key={c._id} style={{ fontSize: 12.5 }}>
                    <span style={{ fontWeight: 600 }}>{authorName(c.author)}</span>
                    <span style={{ color: 'var(--ink-4)', marginLeft: 6 }}>
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''}
                    </span>
                    <div style={{ color: 'var(--ink-2)', marginTop: 1 }}>
                        {c.body || <em style={{ color: 'var(--ink-4)' }}>(deleted)</em>}
                    </div>
                </div>
            ))}
        </div>
    );
}

// Strip the numeric inline-tag tokens (<1>…</1>) the editor uses, and decode the
// HTML entities sections are stored with, so the preview shows clean reading
// text the way the audience will see it. Mirrors TagTextField's tag regex.
export function cleanText(str) {
    if (typeof str !== 'string') return '';
    return str
        .replace(/<\/?\d+>/g, '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
}

// Per-status accent colour for the left border when the Status overlay is on.
const STATUS_COLOR = {
    approved: 'var(--ok, #1a7f37)',
    translated: 'var(--accent, #2563eb)',
    untranslated: 'var(--ink-4)',
};

// Element for a block by its structural type. v1 only emits 'paragraph'; the
// switch is here so heading/list/cell typing can be added without touching the
// frame component.
function blockTag(type) {
    switch (type) {
        case 'heading': return 'h3';
        case 'listItem': return 'li';
        default: return 'p';
    }
}

export function Block({
    block, sourceLang, showSource, showStatus, showConfidence, showTerms, showPseudo,
    override = {}, busy = false, onSave, onToggleApprove,
    matchers, commentCount = 0, getThread, baselineTarget,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [showThread, setShowThread] = useState(false);

    // Effective values fold in optimistic overrides from useSegmentMutations.
    const rawTarget = override.target != null ? override.target : block.target;
    const approved = override.approved != null
        ? override.approved
        : block.status === 'approved';
    const status = approved
        ? 'approved'
        : (rawTarget && rawTarget.trim() ? 'translated' : 'untranslated');

    useEffect(() => {
        if (!editing) setDraft(rawTarget || '');
    }, [rawTarget, editing]);

    const Tag = blockTag(block.type);
    const accent = showStatus ? STATUS_COLOR[status] || 'var(--ink-4)' : 'transparent';

    // Version compare: baselineTarget is set only when a baseline is selected.
    const comparing = baselineTarget !== undefined && baselineTarget !== null;
    const changed = comparing && cleanText(baselineTarget) !== cleanText(rawTarget);

    const startEdit = () => { setDraft(rawTarget || ''); setEditing(true); };
    const commit = async () => {
        if (draft === (rawTarget || '')) { setEditing(false); return; }
        const ok = await onSave(block.sectionIndex, draft);
        if (ok) setEditing(false);
    };
    const cancel = () => { setEditing(false); setDraft(rawTarget || ''); };

    return (
        <div
            style={{
                marginBottom: 14,
                paddingLeft: showStatus ? 10 : 0,
                borderLeft: showStatus ? `3px solid ${accent}` : 'none',
            }}
        >
            {showSource && (
                <p style={{
                    fontSize: 11, color: 'var(--ink-4)', margin: 0,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                    {(sourceLang || 'EN').toUpperCase()}: {cleanText(block.source)}
                </p>
            )}

            {editing ? (
                <div style={{ margin: showSource ? '4px 0 0' : 0 }}>
                    <textarea
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit();
                            if (e.key === 'Escape') cancel();
                        }}
                        rows={Math.min(6, Math.max(2, Math.ceil((draft.length || 1) / 60)))}
                        style={{
                            width: '100%', fontSize: 14, lineHeight: 1.6, padding: 8,
                            border: '1px solid var(--accent, #2563eb)', borderRadius: 8,
                            resize: 'vertical', fontFamily: 'inherit',
                        }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button type="button" className="pv-btn sm primary" onClick={commit} disabled={busy}>
                            {busy ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className="pv-btn sm" onClick={cancel} disabled={busy}>
                            Cancel
                        </button>
                        <span style={{ fontSize: 11, color: 'var(--ink-4)', alignSelf: 'center' }}>
                            ⌘/Ctrl+Enter to save · Esc to cancel
                        </span>
                    </div>
                </div>
            ) : (
                <Tag
                    onClick={onSave ? startEdit : undefined}
                    title={onSave ? 'Click to edit' : undefined}
                    style={{
                        fontSize: 14, lineHeight: 1.6, margin: showSource ? '4px 0 0' : 0,
                        cursor: onSave ? 'text' : 'default',
                        borderRadius: 4,
                    }}
                >
                    {rawTarget && rawTarget.trim()
                        ? (showPseudo
                            ? pseudoLocalize(cleanText(rawTarget))
                            : highlightText(cleanText(rawTarget), showTerms ? matchers : null))
                        : <span style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>— Not translated —</span>}
                </Tag>
            )}

            {changed && (
                <div style={{ marginTop: 4 }}>
                    <span style={{
                        fontSize: 10.5, color: 'var(--warn, #d4a72c)', fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                        Changed
                    </span>
                    <p style={{
                        fontSize: 12.5, lineHeight: 1.5, margin: '2px 0 0',
                        color: 'var(--ink-4)', textDecoration: 'line-through',
                    }}>
                        {cleanText(baselineTarget) || <em>(was empty)</em>}
                    </p>
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 5 }}>
                {onToggleApprove && (
                    <button
                        type="button"
                        className={approved ? 'pv-btn sm primary' : 'pv-btn sm'}
                        onClick={() => onToggleApprove(block.sectionIndex, !approved)}
                        disabled={busy || (!rawTarget || !rawTarget.trim())}
                    >
                        {approved ? '✓ Approved' : 'Approve'}
                    </button>
                )}
                {showConfidence && block.confidence != null && (
                    <span style={{
                        fontSize: 10.5, color: 'var(--ink-3)', background: 'var(--bg-soft)',
                        border: '1px solid var(--line)', borderRadius: 6, padding: '1px 6px',
                    }}>
                        {block.confidence}%{block.translationSource ? ` · ${block.translationSource}` : ''}
                    </span>
                )}
                {commentCount > 0 && getThread && (
                    <button
                        type="button"
                        className="pv-btn sm"
                        onClick={() => setShowThread((v) => !v)}
                    >
                        💬 {commentCount}
                    </button>
                )}
            </div>

            {showThread && getThread && (
                <CommentThread getThread={getThread} sectionIndex={block.sectionIndex} />
            )}
        </div>
    );
}
